import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  SafeAreaView,
  ScrollView,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useIconTheme } from '../contexts/IconThemeContext';
import { useToast } from '../contexts/ToastContext';
import { listHotspots, deleteHotspot, listClubs, deleteMyClub, deleteUserAdmin } from '../services/database';
import { useAuth } from '../services/AuthContext';

const ManageHotspotsScreen = ({ navigation }) => {
  const { getCategoryIcon } = useIconTheme();
  const { appUser } = useAuth();
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myClubs, setMyClubs] = useState([]);
  const [allClubs, setAllClubs] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(true);
  const [selectedCity, setSelectedCity] = useState('ALL');
  const [groupByCity, setGroupByCity] = useState(false);
  const { showToast } = useToast();
  
  // Check if user is admin
  const isAdmin = appUser?.isAdmin === true;

  useEffect(() => {
    fetchHotspots();
    fetchClubs();
  }, []);

  // Refresh hotspots and clubs when screen comes into focus
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      fetchHotspots();
      fetchClubs();
    });
    return unsubscribe;
  }, [navigation]);

  const fetchClubs = async () => {
    try {
      setLoadingClubs(true);
      const [my, all] = await Promise.all([
        appUser?.id ? listClubs({ claimedBy: appUser.id }) : Promise.resolve([]),
        isAdmin ? listClubs() : Promise.resolve([]),
      ]);
      setMyClubs(my || []);
      setAllClubs(all || []);
    } catch (error) {
      console.error('Error fetching clubs:', error);
      showToast('Failed to load clubs', 'error');
    } finally {
      setLoadingClubs(false);
    }
  };

  const handleDeleteClub = async (club, asAdmin = false) => {
    const name = club.club_name || 'This club';
    if (!confirm(`Are you sure you want to remove "${name}" from the map? This cannot be undone.`)) return;
    try {
      if (asAdmin && isAdmin && appUser?.clerkId) {
        await deleteUserAdmin(club.id, appUser.clerkId);
      } else {
        await deleteMyClub(club.id, appUser?.id);
      }
      showToast('Club removed from map', 'success');
      fetchClubs();
    } catch (error) {
      console.error('Error deleting club:', error);
      showToast(error?.message || 'Failed to remove club', 'error');
    }
  };

  const fetchHotspots = async () => {
    try {
      setLoading(true);
      const fetchedHotspots = await listHotspots();
      // Sort by creation date (newest first)
      fetchedHotspots.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setHotspots(fetchedHotspots);
    } catch (error) {
      console.error('Error fetching hotspots:', error);
      showToast('Failed to load hotspots', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHotspot = async (hotspotId, hotspotName) => {
    if (confirm(`Are you sure you want to delete "${hotspotName}"?`)) {
      try {
        await deleteHotspot(hotspotId);
        showToast('Hotspot deleted successfully', 'success');
        fetchHotspots();
      } catch (error) {
        console.error('Error deleting hotspot:', error);
        showToast('Failed to delete hotspot', 'error');
      }
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      PARK: 'Park',
      CAFE: 'Cafe',
      BAR: 'Bar',
      CLUB: 'Chess Club',
      UNIVERSITY: 'University',
      PUBLIC_SQUARE: 'Public Square',
      OTHER: 'Other',
    };
    return labels[category] || category;
  };

  // Get unique cities from hotspots
  const getCities = () => {
    const cities = new Set();
    hotspots.forEach(h => {
      if (h.city) cities.add(h.city);
    });
    return ['ALL', ...Array.from(cities).sort()];
  };

  // Filter hotspots by selected city
  const getFilteredHotspots = () => {
    if (selectedCity === 'ALL') return hotspots;
    return hotspots.filter(h => h.city === selectedCity);
  };

  // Group hotspots by city
  const getGroupedHotspots = () => {
    const grouped = {};
    const filteredHotspots = getFilteredHotspots();
    
    filteredHotspots.forEach(hotspot => {
      const city = hotspot.city || 'Uncategorized';
      if (!grouped[city]) {
        grouped[city] = [];
      }
      grouped[city].push(hotspot);
    });
    
    return grouped;
  };

  const displayHotspots = groupByCity ? null : getFilteredHotspots();
  const groupedHotspots = groupByCity ? getGroupedHotspots() : null;

  const renderHotspotItem = ({ item }) => (
    <TouchableOpacity
      style={styles.hotspotCard}
      onPress={() => navigation.navigate('AddEditHotspot', { hotspot: item })}
      activeOpacity={0.7}
    >
      <View style={styles.hotspotHeader}>
        <View style={styles.hotspotIconContainer}>
          <Text style={styles.hotspotIcon}>{getCategoryIcon(item.category)}</Text>
        </View>
        <View style={styles.hotspotInfo}>
          <Text style={styles.hotspotName}>{item.name}</Text>
          <View style={styles.tagsRow}>
            <View style={styles.categoryBadge}>
              <Text style={styles.categoryText}>{getCategoryLabel(item.category)}</Text>
            </View>
            {item.city && (
              <View style={styles.cityBadge}>
                <Text style={styles.cityText}>{item.city}</Text>
              </View>
            )}
          </View>
          <Text style={styles.hotspotAddress} numberOfLines={1}>
            📍 {item.address}
          </Text>
        </View>
      </View>

      <View style={styles.hotspotActions}>
        <TouchableOpacity
          style={styles.editButton}
          onPress={() => navigation.navigate('AddEditHotspot', { hotspot: item })}
        >
          <Icon name="edit" size={18} color="#94EB98" />
          <Text style={styles.editButtonText}>Edit</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.deleteButton}
          onPress={() => handleDeleteHotspot(item.id, item.name)}
        >
          <Icon name="delete" size={18} color="#f44336" />
          <Text style={styles.deleteButtonText}>Delete</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Icon name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Manage Hotspots</Text>
        <TouchableOpacity
          style={styles.addButton}
          onPress={() => navigation.navigate('AddEditHotspot', { hotspot: null })}
        >
          <Icon name="add" size={28} color="#94EB98" />
        </TouchableOpacity>
      </View>

      {/* Filter and View Controls */}
      {!loading && hotspots.length > 0 && (
        <View style={styles.controlsContainer}>
          {/* City Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterLabel}>Filter by City:</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.cityFilters}>
              {getCities().map(city => (
                <TouchableOpacity
                  key={city}
                  style={[
                    styles.cityFilterButton,
                    selectedCity === city && styles.cityFilterButtonActive
                  ]}
                  onPress={() => setSelectedCity(city)}
                >
                  <Text style={[
                    styles.cityFilterText,
                    selectedCity === city && styles.cityFilterTextActive
                  ]}>
                    {city}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          {/* View Toggle */}
          <View style={styles.viewToggleContainer}>
            <TouchableOpacity
              style={[styles.viewToggleButton, !groupByCity && styles.viewToggleButtonActive]}
              onPress={() => setGroupByCity(false)}
            >
              <Icon name="view-list" size={18} color={!groupByCity ? '#94EB98' : '#666'} />
              <Text style={[styles.viewToggleText, !groupByCity && styles.viewToggleTextActive]}>
                List
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.viewToggleButton, groupByCity && styles.viewToggleButtonActive]}
              onPress={() => setGroupByCity(true)}
            >
              <Icon name="view-module" size={18} color={groupByCity ? '#94EB98' : '#666'} />
              <Text style={[styles.viewToggleText, groupByCity && styles.viewToggleTextActive]}>
                Group by City
              </Text>
            </TouchableOpacity>
          </View>

          {/* Bulk Import Button */}
          <TouchableOpacity
            style={styles.bulkImportButton}
            onPress={() => navigation.navigate('BulkImportHotspots')}
          >
            <Icon name="cloud-upload" size={20} color="#2196F3" />
            <Text style={styles.bulkImportText}>Bulk Import from JSON</Text>
            <Icon name="chevron-right" size={20} color="#2196F3" />
          </TouchableOpacity>
          
          {/* Admin Tools Button - only for admins */}
          {isAdmin && (
            <TouchableOpacity
              style={styles.adminToolsButton}
              onPress={() => navigation.navigate('AdminHotspotsList')}
            >
              <Icon name="admin-panel-settings" size={20} color="#9C27B0" />
              <Text style={styles.adminToolsText}>Admin Tools (AI Suggest, Batch Manage)</Text>
              <Icon name="chevron-right" size={20} color="#9C27B0" />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Clubs on map (club accounts - separate from hotspots) */}
      {!loadingClubs && (myClubs.length > 0 || (isAdmin && allClubs.length > 0)) && (
        <View style={styles.clubsSection}>
          <Text style={styles.clubsSectionTitle}>Clubs on map</Text>
          <Text style={styles.clubsSectionSubtitle}>
            These are club accounts that appear as pins on the map. You can remove test clubs here.
          </Text>
          {myClubs.length > 0 && (
            <>
              <Text style={styles.clubsSubsectionTitle}>My clubs</Text>
              {myClubs.map((club) => (
                <View key={club.id} style={styles.clubCard}>
                  <View style={styles.clubInfo}>
                    <Icon name="group" size={24} color="#666" style={styles.clubIcon} />
                    <View style={styles.clubDetails}>
                      <Text style={styles.clubName}>{club.club_name || 'Unnamed club'}</Text>
                      {(club.address || club.city) && (
                        <Text style={styles.clubAddress} numberOfLines={1}>
                          📍 {[club.address, club.city].filter(Boolean).join(', ')}
                        </Text>
                      )}
                    </View>
                  </View>
                  <TouchableOpacity
                    style={styles.clubDeleteButton}
                    onPress={() => handleDeleteClub(club, false)}
                  >
                    <Icon name="delete" size={18} color="#f44336" />
                    <Text style={styles.clubDeleteButtonText}>Remove</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </>
          )}
          {isAdmin && allClubs.length > 0 && (
            <>
              <Text style={[styles.clubsSubsectionTitle, { marginTop: myClubs.length > 0 ? 16 : 0 }]}>
                All clubs {myClubs.length > 0 ? '(admin)' : ''}
              </Text>
              {allClubs.map((club) => (
                <View key={club.id} style={styles.clubCard}>
                  <View style={styles.clubInfo}>
                    <Icon name="group" size={24} color="#666" style={styles.clubIcon} />
                    <View style={styles.clubDetails}>
                      <Text style={styles.clubName}>{club.club_name || 'Unnamed club'}</Text>
                      {(club.address || club.city) && (
                        <Text style={styles.clubAddress} numberOfLines={1}>
                          📍 {[club.address, club.city].filter(Boolean).join(', ')}
                        </Text>
                      )}
                    </View>
                  </View>
                  <TouchableOpacity
                    style={styles.clubDeleteButton}
                    onPress={() => handleDeleteClub(club, true)}
                  >
                    <Icon name="delete" size={18} color="#f44336" />
                    <Text style={styles.clubDeleteButtonText}>Remove</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </>
          )}
        </View>
      )}

      {/* Content */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <LoadingAnimation size={525} />
        </View>
      ) : hotspots.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Icon name="place" size={80} color="#ccc" />
          <Text style={styles.emptyText}>No hotspots yet</Text>
          <TouchableOpacity
            style={styles.createFirstButton}
            onPress={() => navigation.navigate('AddEditHotspot', { hotspot: null })}
          >
            <Icon name="add-circle" size={24} color="white" />
            <Text style={styles.createFirstButtonText}>Create First Hotspot</Text>
          </TouchableOpacity>
        </View>
      ) : groupByCity ? (
        <ScrollView style={styles.groupedContainer} showsVerticalScrollIndicator={false}>
          {Object.entries(groupedHotspots).map(([city, cityHotspots]) => (
            <View key={city} style={styles.cityGroup}>
              <View style={styles.cityGroupHeader}>
                <Text style={styles.cityGroupTitle}>{city}</Text>
                <View style={styles.cityGroupCount}>
                  <Text style={styles.cityGroupCountText}>{cityHotspots.length}</Text>
                </View>
              </View>
              {cityHotspots.map(hotspot => (
                <View key={hotspot.id}>
                  {renderHotspotItem({ item: hotspot })}
                </View>
              ))}
            </View>
          ))}
          <View style={{ height: 40 }} />
        </ScrollView>
      ) : (
        <FlatList
          data={displayHotspots}
          renderItem={renderHotspotItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
    textAlign: 'center',
  },
  addButton: {
    padding: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#666',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 18,
    color: '#999',
    marginBottom: 24,
  },
  createFirstButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#94EB98',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 10,
  },
  createFirstButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  listContent: {
    padding: 16,
  },
  hotspotCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  hotspotHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  hotspotIconContainer: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#FFF3E0',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  hotspotIcon: {
    fontSize: 28,
  },
  hotspotInfo: {
    flex: 1,
  },
  hotspotName: {
    fontSize: 17,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 4,
  },
  categoryBadge: {
    backgroundColor: '#FF9800',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
  },
  categoryText: {
    fontSize: 11,
    fontWeight: '600',
    color: 'white',
  },
  cityBadge: {
    backgroundColor: '#2196F3',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
  },
  cityText: {
    fontSize: 11,
    fontWeight: '600',
    color: 'white',
  },
  hotspotAddress: {
    fontSize: 13,
    color: '#666',
  },
  hotspotActions: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  editButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#E8F5E9',
  },
  editButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#94EB98',
  },
  deleteButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#FFEBEE',
  },
  deleteButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#f44336',
  },
  controlsContainer: {
    backgroundColor: 'white',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  clubsSection: {
    backgroundColor: '#fff8e1',
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#ffe082',
  },
  clubsSectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  clubsSectionSubtitle: {
    fontSize: 13,
    color: '#666',
    marginBottom: 12,
  },
  clubsSubsectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#555',
    marginBottom: 8,
  },
  clubCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    padding: 12,
    borderRadius: 10,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#f0f0f0',
  },
  clubInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  clubIcon: {
    marginRight: 10,
  },
  clubDetails: {
    flex: 1,
  },
  clubName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  clubAddress: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  clubDeleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  clubDeleteButtonText: {
    fontSize: 14,
    color: '#f44336',
    fontWeight: '500',
  },
  filterSection: {
    marginBottom: 12,
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#666',
    marginBottom: 8,
  },
  cityFilters: {
    flexDirection: 'row',
  },
  cityFilterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#f5f5f5',
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  cityFilterButtonActive: {
    backgroundColor: '#E8F5E9',
    borderColor: '#94EB98',
  },
  cityFilterText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#666',
  },
  cityFilterTextActive: {
    color: '#94EB98',
    fontWeight: '600',
  },
  viewToggleContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  viewToggleButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  viewToggleButtonActive: {
    backgroundColor: '#E8F5E9',
    borderColor: '#94EB98',
  },
  viewToggleText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#666',
  },
  viewToggleTextActive: {
    color: '#94EB98',
    fontWeight: '600',
  },
  bulkImportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#E3F2FD',
    padding: 14,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#2196F3',
    borderStyle: 'dashed',
  },
  bulkImportText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: '#2196F3',
    marginLeft: 8,
  },
  adminToolsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F3E5F5',
    padding: 14,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#9C27B0',
    borderStyle: 'dashed',
    marginTop: 10,
  },
  adminToolsText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: '#9C27B0',
    marginLeft: 8,
  },
  groupedContainer: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  cityGroup: {
    marginBottom: 12,
  },
  cityGroupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: '#94EB98',
  },
  cityGroupTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  cityGroupCount: {
    backgroundColor: '#94EB98',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  cityGroupCountText: {
    fontSize: 13,
    fontWeight: 'bold',
    color: 'white',
  },
});

export default ManageHotspotsScreen;

